import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { useStableAuth } from '@/hooks/useStableAuth';
import { useToast } from '@/hooks/use-toast';
import { BankTransaction } from '@/hooks/useBankTransactions';

interface CreateTransactionFromBankParams {
  bankTransaction: BankTransaction;
  transactionType: 'receivable' | 'payable';
  categoryId: string;
  customerId?: string;
  supplierId?: string;
  costCenterId?: string;
  description?: string;
  notes?: string;
}

async function generateTransactionNumber(companyId: string, type: 'receivable' | 'payable'): Promise<string> {
  const prefix = type === 'receivable' ? 'REC' : 'PAG';
  const year = new Date().getFullYear();
  
  const table = type === 'receivable' ? 'accounts_receivable' : 'accounts_payable';
  
  const { count } = await supabase
    .from(table)
    .select('*', { count: 'exact', head: true })
    .eq('company_id', companyId)
    .gte('created_at', `${year}-01-01`);
  
  const sequence = ((count || 0) + 1).toString().padStart(6, '0');
  return `${prefix}-${year}-${sequence}`;
}

export const useCreateTransactionFromBank = () => {
  const { companyId } = useMultiTenant();
  const { userId } = useStableAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateTransactionFromBankParams) => {
      if (!companyId) throw new Error('Company ID não disponível');
      if (!userId) throw new Error('Usuário não autenticado');

      const {
        bankTransaction,
        transactionType,
        categoryId,
        customerId,
        supplierId,
        costCenterId,
        description,
        notes,
      } = params;

      const transactionNumber = await generateTransactionNumber(companyId, transactionType);
      const transactionDate = bankTransaction.transaction_date;

      // F2 (D-140C-iii.1): chave E2E da linha — vira p_external_key da RPC
      // (idempotencia por chave forte; o tipo BankTransaction nao carrega o campo)
      const { data: btKeyRow } = await supabase
        .from('bank_transactions')
        .select('pix_end_to_end_id')
        .eq('id', bankTransaction.id)
        .single();
      const externalKey = btKeyRow?.pix_end_to_end_id ?? null;

      if (transactionType === 'receivable') {
        // Criar conta a receber já paga
        const { data: receivable, error: receivableError } = await supabase
          .from('accounts_receivable')
          .insert({
            company_id: companyId,
            transaction_number: transactionNumber,
            customer_id: customerId,
            income_category_id: categoryId,
            description: description || bankTransaction.description,
            issue_date: transactionDate,
            due_date: transactionDate,
            payment_date: transactionDate,
            original_value: bankTransaction.value,
            total_value: bankTransaction.value,
            paid_value: bankTransaction.value,
            status: 'paid',
            financial_account_id: bankTransaction.financial_account_id,
            notes: notes || `Lançamento criado a partir de transação bancária: ${bankTransaction.external_id}`,
            created_by: userId,
          })
          .select()
          .single();

        if (receivableError) throw receivableError;

        // F2 (D-140C-iii.1): baixa via RPC (idempotente por chave forte) —
        // cura do PGRST204: fim do INSERT direto com company_id fantasma
        const { data: baixa, error: baixaError } = await supabase.rpc(
          'register_bank_receivable_payment',
          {
            p_receivable_id: receivable.id,
            p_payment_value: bankTransaction.value,
            p_payment_date: transactionDate,
            p_payment_method: 'bank_transfer',
            p_external_key: externalKey ?? undefined,
            p_notes: `Pagamento automático - transação bancária ${bankTransaction.external_id}`,
            p_financial_account_id: bankTransaction.financial_account_id,
          }
        );

        if (baixaError) throw baixaError;
        const paymentId = (baixa as { payment_id?: string } | null)?.payment_id;
        if (!paymentId) throw new Error('Baixa registrada sem payment_id retornado pela RPC');

        // Vínculo bidirecional (F2): payment primeiro, linha depois (ordem R3)
        const { error: linkError } = await supabase
          .from('receivable_payments')
          .update({ bank_transaction_id: bankTransaction.id })
          .eq('id', paymentId);

        if (linkError) throw linkError;

        // Marcar transação bancária como conciliada
        const { error: reconcileError } = await supabase
          .from('bank_transactions')
          .update({
            reconciliation_status: 'reconciled',
            reconciled_at: new Date().toISOString(),
            reconciled_by: userId,
            receivable_payment_id: paymentId,
          })
          .eq('id', bankTransaction.id);

        if (reconcileError) throw reconcileError;

        return { receivable, paymentId };
      } else {
        // Criar conta a pagar já paga
        const { data: payable, error: payableError } = await supabase
          .from('accounts_payable')
          .insert({
            company_id: companyId,
            transaction_number: transactionNumber,
            supplier_id: supplierId,
            expense_category_id: categoryId,
            cost_center_id: costCenterId,
            description: description || bankTransaction.description,
            issue_date: transactionDate,
            due_date: transactionDate,
            payment_date: transactionDate,
            original_value: bankTransaction.value,
            total_value: bankTransaction.value,
            paid_value: bankTransaction.value,
            status: 'paid',
            financial_account_id: bankTransaction.financial_account_id,
            notes: notes || `Lançamento criado a partir de transação bancária: ${bankTransaction.external_id}`,
            created_by: userId,
          })
          .select()
          .single();

        if (payableError) throw payableError;

        // F2 (D-140C-iii.1): baixa via RPC da Peça 2 (idempotente por chave
        // forte) — cura do PGRST204: fim do INSERT direto com company_id fantasma
        const { data: baixa, error: baixaError } = await supabase.rpc(
          'register_bank_payable_payment',
          {
            p_payable_id: payable.id,
            p_payment_value: bankTransaction.value,
            p_payment_date: transactionDate,
            p_payment_method: 'bank_transfer',
            p_external_key: externalKey ?? undefined,
            p_notes: `Pagamento automático - transação bancária ${bankTransaction.external_id}`,
            p_financial_account_id: bankTransaction.financial_account_id,
          }
        );

        if (baixaError) throw baixaError;
        const paymentId = (baixa as { payment_id?: string } | null)?.payment_id;
        if (!paymentId) throw new Error('Baixa registrada sem payment_id retornado pela RPC');

        // Vínculo bidirecional (F2): payment primeiro, linha depois (ordem R3)
        const { error: linkError } = await supabase
          .from('payable_payments')
          .update({ bank_transaction_id: bankTransaction.id })
          .eq('id', paymentId);

        if (linkError) throw linkError;

        // Marcar transação bancária como conciliada
        const { error: reconcileError } = await supabase
          .from('bank_transactions')
          .update({
            reconciliation_status: 'reconciled',
            reconciled_at: new Date().toISOString(),
            reconciled_by: userId,
            payable_payment_id: paymentId,
          })
          .eq('id', bankTransaction.id);

        if (reconcileError) throw reconcileError;

        return { payable, paymentId };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-receivable'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-payable'] });
      queryClient.invalidateQueries({ queryKey: ['account-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['financial-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['financial-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast({ title: 'Lançamento criado e conciliado com sucesso!' });
    },
    onError: (error: Error) => {
      console.error('Erro ao criar lançamento:', error);
      toast({
        title: 'Erro ao criar lançamento',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};
